/**
 * NodeConfigPanel (SPEC PX-4 — workflow-builder, R2).
 *
 * The side panel that edits a selected node's config. Fields are DRIVEN by the
 * action's JSON schema from GET /api/workflows/metadata (field names, types,
 * defaults, enums) — never a hardcoded form per type — so a NEW action added to
 * the engine's registry surfaces an editable form here with NO canvas code change
 * (the Flexibility AC). The panel writes config FLAT on the node (never under a
 * `config` key).
 *
 * Control-flow fields (branch targets, split weights, wait_for_event resume/
 * timeout) are edited by a dedicated section (BranchEditor / WaitForEventEditor),
 * since they reference other nodes by id rather than scalar values.
 */

import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ActionSchema,
  GraphNode,
  SchemaProperty,
  WorkflowMetadata,
} from '@/types/workflow';
import { TERMINAL_NODE_ID } from '@/types/workflow';

// Control-flow keys are edited by the dedicated editors below, not the generic
// scalar form (they reference node ids).
const CONTROL_FLOW_KEYS = new Set([
  'type',
  'branches',
  'split_branches',
  'default_target_action_index',
  'default_target_node_id',
  'resume_target_action_index',
  'timeout_target_action_index',
  'resume_target_node_id',
  'timeout_target_node_id',
]);

// update_field.collection is constrained to these by the engine allow-list.
const UPDATE_FIELD_COLLECTIONS = ['contacts', 'opportunities'];

function schemaPropType(prop: SchemaProperty): string {
  if (prop.enum) return 'enum';
  // Pydantic Optional -> anyOf [type, null]
  const anyOf = prop.anyOf;
  if (anyOf && anyOf.length) {
    const first = anyOf.find((p) => p.type && p.type !== 'null');
    if (first?.type === 'integer' || first?.type === 'number') return 'number';
    if (first?.type === 'array') return 'array';
    return 'string';
  }
  if (prop.type === 'integer' || prop.type === 'number') return 'number';
  if (prop.type === 'array') return 'array';
  if (prop.type === 'boolean') return 'boolean';
  return 'string';
}

interface NodeConfigPanelProps {
  node: GraphNode;
  metadata?: WorkflowMetadata;
  actionNodes: GraphNode[]; // candidate targets for control-flow editors
  triggerTypes: string[];
  onChange: (nodeId: string, config: Record<string, unknown>) => void;
  onChangeType: (nodeId: string, type: string) => void;
  onDelete?: (nodeId: string) => void;
}

export function NodeConfigPanel({
  node,
  metadata,
  actionNodes,
  triggerTypes,
  onChange,
  onChangeType,
  onDelete,
}: NodeConfigPanelProps) {
  const isTrigger = node.kind === 'trigger';

  const actionSchema: ActionSchema | undefined = metadata?.actions.find(
    (a) => a.type === node.type
  )?.schema;

  const set = (key: string, value: unknown) => {
    onChange(node.id, { ...node.config, [key]: value });
  };

  return (
    <div className="space-y-4" data-testid="node-config-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">
          {isTrigger ? 'Trigger' : `${node.type} step`}
        </h3>
        {!isTrigger && onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={() => onDelete(node.id)}
            data-testid="delete-node-btn"
            title="Delete node"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Type selector. */}
      {isTrigger ? (
        <div>
          <Label>Trigger type</Label>
          <Select value={node.type} onValueChange={(v) => onChangeType(node.id, v)}>
            <SelectTrigger className="mt-1" data-testid="trigger-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {triggerTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div>
          <Label>Action type</Label>
          <Select value={node.type} onValueChange={(v) => onChangeType(node.id, v)}>
            <SelectTrigger className="mt-1" data-testid="action-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(metadata?.actions ?? []).map((a) => (
                <SelectItem key={a.type} value={a.type}>
                  {a.type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Trigger config (type-specific convenience keys from /metadata). */}
      {isTrigger && (
        <TriggerConfigFields node={node} metadata={metadata} set={set} />
      )}

      {/* Branch / split authoring. */}
      {!isTrigger && node.type === 'branch' && (
        <BranchEditor node={node} actionNodes={actionNodes} onChange={onChange} />
      )}

      {/* wait_for_event target authoring. */}
      {!isTrigger && node.type === 'wait_for_event' && (
        <WaitForEventEditor node={node} actionNodes={actionNodes} onChange={onChange} set={set} />
      )}

      {/* Generic schema-driven scalar fields. */}
      {!isTrigger && actionSchema?.properties && (
        <div className="space-y-3">
          {Object.entries(actionSchema.properties)
            .filter(([key]) => !CONTROL_FLOW_KEYS.has(key))
            // wait_for_event scalar fields are rendered inside its editor.
            .filter(([key]) =>
              node.type === 'wait_for_event'
                ? !['event_type', 'timeout_minutes'].includes(key)
                : true
            )
            .map(([key, prop]) => (
              <SchemaField
                key={key}
                fieldKey={key}
                prop={prop}
                nodeType={node.type}
                value={node.config[key]}
                onChange={(v) => set(key, v)}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// --- Trigger config -------------------------------------------------------

function TriggerConfigFields({
  node,
  metadata,
  set,
}: {
  node: GraphNode;
  metadata?: WorkflowMetadata;
  set: (k: string, v: unknown) => void;
}) {
  const fields = metadata?.triggers.find((t) => t.type === node.type)?.config_fields ?? [];
  if (node.type === 'webhook') {
    return (
      <p className="text-xs text-muted-foreground rounded-md border bg-muted/40 p-2">
        The HMAC token is generated server-side on save — the canvas never
        fabricates one.
      </p>
    );
  }
  if (!fields.length) {
    return <p className="text-xs text-muted-foreground">No config for this trigger.</p>;
  }
  return (
    <div className="space-y-3">
      {fields
        .filter((f) => f !== 'hmac_token')
        .map((f) => (
          <div key={f}>
            <Label className="capitalize">{f.replace(/_/g, ' ')}</Label>
            <Input
              className="mt-1"
              data-testid={`trigger-config-${f}`}
              value={(node.config[f] as string) ?? ''}
              onChange={(e) => set(f, e.target.value)}
            />
          </div>
        ))}
    </div>
  );
}

// --- Generic schema-driven scalar field -----------------------------------

function SchemaField({
  fieldKey,
  prop,
  nodeType,
  value,
  onChange,
}: {
  fieldKey: string;
  prop: SchemaProperty;
  nodeType: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const kind = schemaPropType(prop);
  const label = fieldKey.replace(/_/g, ' ');

  // update_field.collection -> constrained select.
  if (nodeType === 'update_field' && fieldKey === 'collection') {
    return (
      <div>
        <Label className="capitalize">{label}</Label>
        <Select value={(value as string) ?? 'contacts'} onValueChange={onChange}>
          <SelectTrigger className="mt-1" data-testid={`field-${fieldKey}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UPDATE_FIELD_COLLECTIONS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (kind === 'array') {
    // Comma-separated list editor (tag ids, round_robin, …).
    const arr = Array.isArray(value) ? (value as unknown[]) : [];
    return (
      <div>
        <Label className="capitalize">{label}</Label>
        <Input
          className="mt-1"
          data-testid={`field-${fieldKey}`}
          placeholder="comma,separated"
          value={arr.join(',')}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
        />
      </div>
    );
  }

  if (kind === 'number') {
    return (
      <div>
        <Label className="capitalize">{label}</Label>
        <Input
          type="number"
          className="mt-1"
          data-testid={`field-${fieldKey}`}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const n = e.target.value === '' ? undefined : Number(e.target.value);
            onChange(Number.isNaN(n as number) ? undefined : n);
          }}
        />
      </div>
    );
  }

  // Long text for message/content/subject; single line otherwise.
  const isLong = ['content', 'message', 'subject'].includes(fieldKey);
  return (
    <div>
      <Label className="capitalize">{label}</Label>
      {isLong ? (
        <Textarea
          className="mt-1"
          data-testid={`field-${fieldKey}`}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          className="mt-1"
          data-testid={`field-${fieldKey}`}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// --- Branch / split editor -------------------------------------------------

interface BranchCfgEntry {
  condition?: Record<string, unknown>;
  target_node_id?: string;
}
interface SplitCfgEntry {
  weight?: number;
  target_node_id?: string;
}

function TargetSelect({
  value,
  actionNodes,
  onChange,
  testid,
}: {
  value?: string;
  actionNodes: GraphNode[];
  onChange: (v: string) => void;
  testid?: string;
}) {
  return (
    <Select value={value || TERMINAL_NODE_ID} onValueChange={onChange}>
      <SelectTrigger className="h-8" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TERMINAL_NODE_ID}>End</SelectItem>
        {actionNodes.map((nd) => (
          <SelectItem key={nd.id} value={nd.id}>
            {nd.type} ({nd.id})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BranchEditor({
  node,
  actionNodes,
  onChange,
}: {
  node: GraphNode;
  actionNodes: GraphNode[];
  onChange: (id: string, config: Record<string, unknown>) => void;
}) {
  const branches = (Array.isArray(node.config.branches) ? node.config.branches : []) as BranchCfgEntry[];
  const split = (Array.isArray(node.config.split_branches) ? node.config.split_branches : []) as SplitCfgEntry[];
  const isSplit = split.length > 0;
  const targets = actionNodes.filter((nd) => nd.id !== node.id);

  const setBranches = (next: BranchCfgEntry[]) =>
    onChange(node.id, { ...node.config, branches: next });
  const setSplit = (next: SplitCfgEntry[]) =>
    onChange(node.id, { ...node.config, split_branches: next });

  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="branch-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase text-muted-foreground">
          {isSplit ? 'Split (weighted)' : 'If / else branches'}
        </Label>
        <div className="flex gap-1">
          <Button
            variant={isSplit ? 'ghost' : 'secondary'}
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              onChange(node.id, {
                ...node.config,
                split_branches: [],
                branches: branches.length
                  ? branches
                  : [{ condition: { type: 'tag_present', tag: '' }, target_node_id: TERMINAL_NODE_ID }],
              })
            }
            data-testid="branch-mode-ifelse"
          >
            If/else
          </Button>
          <Button
            variant={isSplit ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              onChange(node.id, {
                ...node.config,
                branches: [],
                split_branches: split.length
                  ? split
                  : [
                      { weight: 50, target_node_id: TERMINAL_NODE_ID },
                      { weight: 50, target_node_id: TERMINAL_NODE_ID },
                    ],
              })
            }
            data-testid="branch-mode-split"
          >
            Split
          </Button>
        </div>
      </div>

      {isSplit ? (
        <div className="space-y-2">
          {split.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                type="number"
                className="h-8 w-16"
                data-testid={`split-weight-${i}`}
                value={s.weight ?? 1}
                onChange={(e) => {
                  const next = [...split];
                  next[i] = { ...s, weight: Number(e.target.value) || 0 };
                  setSplit(next);
                }}
              />
              <TargetSelect
                value={s.target_node_id}
                actionNodes={targets}
                testid={`split-target-${i}`}
                onChange={(v) => {
                  const next = [...split];
                  next[i] = { ...s, target_node_id: v };
                  setSplit(next);
                }}
              />
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setSplit([...split, { weight: 50, target_node_id: TERMINAL_NODE_ID }])}
          >
            Add split path
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {branches.map((b, i) => (
            <div key={i} className="space-y-1 rounded border p-2">
              <ConditionEditor
                condition={b.condition || {}}
                onChange={(c) => {
                  const next = [...branches];
                  next[i] = { ...b, condition: c };
                  setBranches(next);
                }}
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">then go to</span>
                <TargetSelect
                  value={b.target_node_id}
                  actionNodes={targets}
                  testid={`branch-target-${i}`}
                  onChange={(v) => {
                    const next = [...branches];
                    next[i] = { ...b, target_node_id: v };
                    setBranches(next);
                  }}
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              setBranches([
                ...branches,
                { condition: { type: 'tag_present', tag: '' }, target_node_id: TERMINAL_NODE_ID },
              ])
            }
          >
            Add condition
          </Button>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">else (default)</span>
            <TargetSelect
              value={node.config.default_target_node_id as string}
              actionNodes={targets}
              testid="branch-default-target"
              onChange={(v) => onChange(node.id, { ...node.config, default_target_node_id: v })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const CONDITION_TYPES = [
  'tag_present',
  'field_equals',
  'field_gt',
  'field_lt',
  'field_gte',
  'field_lte',
  'trigger_payload_equals',
];

function ConditionEditor({
  condition,
  onChange,
}: {
  condition: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const ctype = (condition.type as string) || 'tag_present';
  return (
    <div className="space-y-1" data-testid="condition-builder">
      <Select value={ctype} onValueChange={(v) => onChange({ ...condition, type: v })}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONDITION_TYPES.map((c) => (
            <SelectItem key={c} value={c}>
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {ctype === 'tag_present' ? (
        <Input
          className="h-8"
          placeholder="tag id"
          data-testid="condition-tag"
          value={(condition.tag as string) ?? ''}
          onChange={(e) => onChange({ ...condition, tag: e.target.value })}
        />
      ) : ctype === 'trigger_payload_equals' ? (
        <div className="flex gap-2">
          <Input
            className="h-8"
            placeholder="key"
            value={(condition.key as string) ?? ''}
            onChange={(e) => onChange({ ...condition, key: e.target.value })}
          />
          <Input
            className="h-8"
            placeholder="value"
            value={(condition.value as string) ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            className="h-8"
            placeholder="contact.field"
            value={(condition.field as string) ?? ''}
            onChange={(e) => onChange({ ...condition, field: e.target.value })}
          />
          <Input
            className="h-8"
            placeholder="value"
            value={(condition.value as string) ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

// --- wait_for_event editor -------------------------------------------------

function WaitForEventEditor({
  node,
  actionNodes,
  onChange,
  set,
}: {
  node: GraphNode;
  actionNodes: GraphNode[];
  onChange: (id: string, config: Record<string, unknown>) => void;
  set: (k: string, v: unknown) => void;
}) {
  const targets = actionNodes.filter((nd) => nd.id !== node.id);
  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="wait-for-event-editor">
      <div>
        <Label>Event type</Label>
        <Input
          className="mt-1"
          data-testid="wfe-event-type"
          value={(node.config.event_type as string) ?? ''}
          onChange={(e) => set('event_type', e.target.value)}
        />
      </div>
      <div>
        <Label>Timeout (minutes)</Label>
        <Input
          type="number"
          className="mt-1"
          data-testid="wfe-timeout-minutes"
          value={(node.config.timeout_minutes as number) ?? 1440}
          onChange={(e) => set('timeout_minutes', Number(e.target.value) || 0)}
        />
      </div>
      <div>
        <Label className="text-xs">On event, go to</Label>
        <TargetSelect
          value={node.config.resume_target_node_id as string}
          actionNodes={targets}
          testid="wfe-resume-target"
          onChange={(v) => onChange(node.id, { ...node.config, resume_target_node_id: v })}
        />
      </div>
      <div>
        <Label className="text-xs">On timeout, go to</Label>
        <TargetSelect
          value={node.config.timeout_target_node_id as string}
          actionNodes={targets}
          testid="wfe-timeout-target"
          onChange={(v) => onChange(node.id, { ...node.config, timeout_target_node_id: v })}
        />
      </div>
    </div>
  );
}
